import { hasDealerLaunchDate } from 'utils/dealerLaunchDate';
import { parseCalendarDateFromApi } from 'utils/dateUtils';

export type DealerLaunchMaterialization = {
  status?: string;
  job_id?: string;
  pending_launch_date?: string;
  plan_id?: string;
  started_at?: string;
  error?: string;
  failed_at?: string;
  channels_total?: number;
  channels_completed?: number;
  active_days_created?: number;
  active_days_total?: number;
};

export type DealerLaunchAck = {
  job_id?: string;
  dealer_id?: string;
  status?: string;
  pending_launch_date?: string;
  plan_id?: string;
  plan_version?: number | null;
};

export type DealerLaunchRequestReceivedMessage = {
  job_id?: string;
  dealer_id?: string;
  pending_launch_date?: string;
  plan_id?: string;
  plan_version?: number | null;
  active_channel_count?: number;
};

export type DealerLaunchProgressMessage = {
  job_id?: string;
  dealer_id?: string;
  pending_launch_date?: string;
  channels_total?: number;
  channels_completed?: number;
  channel_id?: string;
  active_days_created?: number;
  active_days_total?: number;
};

export type DealerLaunchCompletedMessage = {
  job_id?: string;
  dealer_id?: string;
  status?: 'COMPLETED' | 'FAILED' | string;
  launch_date?: string;
  plan_id?: string;
  plan_version?: number;
  channels_materialized?: number;
  active_days_created?: number;
  error?: string;
};

export function extractDealerLaunchSocketMessage<T extends Record<string, unknown>>(
  data: unknown,
): T | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const payload = data as { message?: unknown };
  if (payload.message && typeof payload.message === 'object') {
    return payload.message as T;
  }

  return data as T;
}

function extractSocketMessage<T extends Record<string, unknown>>(
  data: unknown,
): T | null {
  return extractDealerLaunchSocketMessage<T>(data);
}

export function getDealerLaunchMaterialization(
  dealer: unknown,
): DealerLaunchMaterialization | null {
  if (typeof dealer !== 'object' || dealer === null) {
    return null;
  }

  const raw = (
    dealer as { additional_attributes?: { launch_materialization?: unknown } }
  ).additional_attributes?.launch_materialization;

  if (!raw || typeof raw !== 'object') {
    return null;
  }

  return raw as DealerLaunchMaterialization;
}

export function getDealerLaunchMaterializationStatus(dealer: unknown): string | null {
  const status = getDealerLaunchMaterialization(dealer)?.status;
  if (typeof status !== 'string') {
    return null;
  }

  const trimmed = status.trim();
  return trimmed || null;
}

export function shouldShowDealerLaunchMaterializationStatus(dealer: unknown): boolean {
  if (hasDealerLaunchDate(dealer)) {
    return false;
  }

  return Boolean(getDealerLaunchMaterializationStatus(dealer));
}

export function buildLaunchMaterializationFromLaunchAck(
  ack: DealerLaunchAck | null | undefined,
): DealerLaunchMaterialization | null {
  if (!ack?.job_id?.trim()) {
    return null;
  }

  return {
    status: 'IN_PROGRESS',
    job_id: ack.job_id.trim(),
    pending_launch_date: ack.pending_launch_date?.trim() || undefined,
    plan_id: ack.plan_id?.trim() || undefined,
    started_at: new Date().toISOString(),
  };
}

function mergeDealerAdditionalAttributes(
  dealer: unknown,
  launchMaterialization: DealerLaunchMaterialization | null,
): Record<string, unknown> {
  const base =
    typeof dealer === 'object' &&
    dealer !== null &&
    typeof (dealer as { additional_attributes?: unknown }).additional_attributes ===
      'object'
      ? {
          ...((dealer as { additional_attributes?: Record<string, unknown> })
            .additional_attributes ?? {}),
        }
      : {};

  if (launchMaterialization == null) {
    const { launch_materialization: _removed, ...rest } = base;
    return rest;
  }

  return {
    ...base,
    launch_materialization: launchMaterialization,
  };
}

export function mergeDealerWithLaunchAck(
  dealer: unknown,
  ack: DealerLaunchAck | null | undefined,
): Record<string, unknown> | null {
  const materialization = buildLaunchMaterializationFromLaunchAck(ack);
  if (!materialization) {
    return null;
  }

  const dealerId =
    ack?.dealer_id?.trim() ||
    (typeof dealer === 'object' && dealer !== null
      ? String(
          (dealer as { _id?: string; dealer?: string })._id ||
            (dealer as { dealer?: string }).dealer ||
            '',
        ).trim()
      : '');

  const base =
    typeof dealer === 'object' && dealer !== null
      ? { ...(dealer as Record<string, unknown>) }
      : {};

  return {
    ...base,
    ...(dealerId ? { dealer: dealerId } : {}),
    additional_attributes: mergeDealerAdditionalAttributes(dealer, materialization),
  };
}

export function mergeLaunchRequestReceivedIntoDealer(
  dealer: unknown,
  data: unknown,
): Record<string, unknown> | null {
  const message = extractSocketMessage<DealerLaunchRequestReceivedMessage>(data);
  if (!message?.job_id?.trim()) {
    return null;
  }

  const existing = getDealerLaunchMaterialization(dealer);
  if (existing?.job_id && existing.job_id !== message.job_id) {
    return null;
  }

  const materialization: DealerLaunchMaterialization = {
    ...(existing ?? {}),
    status: 'IN_PROGRESS',
    job_id: message.job_id.trim(),
    pending_launch_date:
      message.pending_launch_date?.trim() || existing?.pending_launch_date,
    plan_id: message.plan_id?.trim() || existing?.plan_id,
    started_at: existing?.started_at ?? new Date().toISOString(),
  };

  const base =
    typeof dealer === 'object' && dealer !== null
      ? { ...(dealer as Record<string, unknown>) }
      : {};

  return {
    ...base,
    additional_attributes: mergeDealerAdditionalAttributes(dealer, materialization),
  };
}

export function mergeLaunchProgressIntoDealer(
  dealer: unknown,
  data: unknown,
): Record<string, unknown> | null {
  const message = extractSocketMessage<DealerLaunchProgressMessage>(data);
  if (!message) {
    return null;
  }

  const existing = getDealerLaunchMaterialization(dealer);
  const trackedJobId = existing?.job_id?.trim();
  const eventJobId = message.job_id?.trim();

  if (trackedJobId && eventJobId && trackedJobId !== eventJobId) {
    return null;
  }

  const materialization: DealerLaunchMaterialization = {
    ...(existing ?? {
      status: 'IN_PROGRESS',
      job_id: eventJobId,
      started_at: new Date().toISOString(),
    }),
    status: 'IN_PROGRESS',
    job_id: eventJobId || existing?.job_id,
    pending_launch_date:
      message.pending_launch_date?.trim() || existing?.pending_launch_date,
    channels_total: message.channels_total ?? existing?.channels_total,
    channels_completed: message.channels_completed ?? existing?.channels_completed,
    active_days_created:
      message.active_days_created ?? existing?.active_days_created,
    active_days_total: message.active_days_total ?? existing?.active_days_total,
  };

  const base =
    typeof dealer === 'object' && dealer !== null
      ? { ...(dealer as Record<string, unknown>) }
      : {};

  return {
    ...base,
    additional_attributes: mergeDealerAdditionalAttributes(dealer, materialization),
  };
}

export function mergeLaunchCompletedIntoDealer(
  dealer: unknown,
  data: unknown,
): Record<string, unknown> | null {
  const message = extractSocketMessage<DealerLaunchCompletedMessage>(data);
  if (!message?.status?.trim()) {
    return null;
  }

  const existing = getDealerLaunchMaterialization(dealer);
  const trackedJobId = existing?.job_id?.trim();
  const eventJobId = message.job_id?.trim();

  if (trackedJobId && eventJobId && trackedJobId !== eventJobId) {
    return null;
  }

  const base =
    typeof dealer === 'object' && dealer !== null
      ? { ...(dealer as Record<string, unknown>) }
      : {};

  const normalizedStatus = message.status.trim().toUpperCase();

  if (normalizedStatus === 'COMPLETED') {
    return {
      ...base,
      launch_date: message.launch_date?.trim() || base.launch_date,
      status: 'ACTIVE',
      additional_attributes: mergeDealerAdditionalAttributes(dealer, null),
    };
  }

  if (normalizedStatus === 'FAILED') {
    const failedMaterialization: DealerLaunchMaterialization = {
      status: 'FAILED',
      job_id: eventJobId || existing?.job_id,
      pending_launch_date:
        message.launch_date?.trim() || existing?.pending_launch_date,
      plan_id: message.plan_id?.trim() || existing?.plan_id,
      error: message.error?.trim() || undefined,
      failed_at: new Date().toISOString(),
      started_at: existing?.started_at,
    };

    return {
      ...base,
      additional_attributes: mergeDealerAdditionalAttributes(
        dealer,
        failedMaterialization,
      ),
    };
  }

  return null;
}

function formatMaterializationStatus(status: string): string {
  const normalized = status.trim().toUpperCase();

  if (normalized === 'IN_PROGRESS') {
    return 'Launch in progress';
  }

  if (normalized === 'FAILED') {
    return 'Launch failed';
  }

  return status
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function appendInProgressProgressLabel(
  label: string,
  materialization: DealerLaunchMaterialization,
): string {
  const channelsTotal = materialization.channels_total;
  const channelsCompleted = materialization.channels_completed;

  if (
    typeof channelsTotal === 'number' &&
    channelsTotal > 0 &&
    typeof channelsCompleted === 'number'
  ) {
    return `${label} (${channelsCompleted}/${channelsTotal} channels)`;
  }

  const activeDaysTotal = materialization.active_days_total;
  const activeDaysCreated = materialization.active_days_created;

  if (
    typeof activeDaysTotal === 'number' &&
    activeDaysTotal > 0 &&
    typeof activeDaysCreated === 'number'
  ) {
    return `${label} (${activeDaysCreated}/${activeDaysTotal} active days)`;
  }

  return label;
}

export function getDealerLaunchMaterializationLabel(dealer: unknown): string {
  const materialization = getDealerLaunchMaterialization(dealer);
  const status = materialization?.status;

  if (!status?.trim()) {
    return 'Launch status unavailable';
  }

  let label = formatMaterializationStatus(status);

  if (status.trim().toUpperCase() === 'FAILED' && materialization?.error?.trim()) {
    return `${label}: ${materialization.error.trim()}`;
  }

  if (status.trim().toUpperCase() === 'IN_PROGRESS' && materialization) {
    label = appendInProgressProgressLabel(label, materialization);
  }

  const pendingDate = materialization?.pending_launch_date?.trim();
  if (pendingDate && status.trim().toUpperCase() === 'IN_PROGRESS') {
    const parsed = parseCalendarDateFromApi(pendingDate);
    if (parsed?.isValid) {
      label = `${label} (scheduled ${parsed.toFormat('MM/dd/yyyy')})`;
    }
  }

  return label;
}

export function getDealerLaunchMaterializationChipColor(
  dealer: unknown,
): 'info' | 'error' | 'warning' | 'default' {
  const status = getDealerLaunchMaterializationStatus(dealer)?.toUpperCase();

  if (status === 'IN_PROGRESS') {
    return 'info';
  }

  if (status === 'FAILED') {
    return 'error';
  }

  return 'warning';
}

export function isDealerLaunchMaterializationInProgress(dealer: unknown): boolean {
  return getDealerLaunchMaterializationStatus(dealer)?.toUpperCase() === 'IN_PROGRESS';
}

export function isDealerLaunchMaterializationFailed(dealer: unknown): boolean {
  return getDealerLaunchMaterializationStatus(dealer)?.toUpperCase() === 'FAILED';
}

export function computeLaunchChannelProgressPercent(
  materialization: DealerLaunchMaterialization | null | undefined,
): number {
  if (!materialization) {
    return 0;
  }

  const total = materialization.channels_total;
  const completed = materialization.channels_completed;

  if (typeof total !== 'number' || total <= 0 || typeof completed !== 'number') {
    return 0;
  }

  return Math.min(100, Math.round((completed / total) * 100));
}

export function computeLaunchActiveDaysProgressPercent(
  materialization: DealerLaunchMaterialization | null | undefined,
): number {
  if (!materialization) {
    return 0;
  }

  const total = materialization.active_days_total;
  const created = materialization.active_days_created;

  if (typeof total !== 'number' || total <= 0 || typeof created !== 'number') {
    return 0;
  }

  return Math.min(100, Math.round((created / total) * 100));
}

export function persistSelectedDealerToLocalStorage(dealer: unknown): void {
  if (typeof dealer !== 'object' || dealer === null) {
    return;
  }

  try {
    localStorage.setItem('dealer', JSON.stringify(dealer));
  } catch {
    // ignore localStorage errors
  }
}

export function readPersistedSelectedDealer(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem('dealer');
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function resolvePersistedDealerId(dealer: Record<string, unknown> | null): string | null {
  if (!dealer) {
    return null;
  }
  const id = dealer.dealer ?? dealer._id;
  if (id == null || id === '') {
    return null;
  }
  return String(id);
}

export function isDealerLaunchInProgressForDealerId(
  dealerId: string | null | undefined,
): boolean {
  if (!dealerId) {
    return false;
  }

  const persisted = readPersistedSelectedDealer();
  const persistedId = resolvePersistedDealerId(persisted);
  if (!persistedId || persistedId !== dealerId) {
    return false;
  }

  return isDealerLaunchMaterializationInProgress(persisted);
}
